import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import {Input as InputForm} from "../../types/Input";
import {InputType} from "../../types/InputType";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../components/Skeletons/FormSkeleton";
import {Currency} from "../../types/Responses/CurrenciesResponse";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {Provider} from "../../types/Responses/ProvidersResponse";
import {BuilderInputType} from "../../types/BuilderInputType";
import InputBuilder from "../../components/InputBuilder";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import {useUserContext} from "../../types/contexts/UserContext";

const InputBuilderForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [provider, setProvider] = useState<string>();
    const [clientInputs, setClientInputs] = useState<InputForm[]>();
    const [credentialsInputs, setCredentialsInputs] = useState<InputForm[]>();
    const [credentialsCurrencyInputs, setCredentialsCurrencyInputs] = useState<InputForm[]>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [type, setType] = useState<"credentials" | "client">("client");
    const variables = [
        'clientId', 'email', 'firstName', 'lastName', 'phone', 'city', 'transactionId', 'country', 'currency', 'lang', 'theme', 'psp'
    ];

    const handleSuccessResponse = (response: AxiosResponse, message: JSX.Element | string) => {
        const currency = response.data.data as Currency
        if (currency) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/providers');
        }
    }

    const submit = () => {
        setInProgress(true);
        setValidated(true);
        if (type === 'client') {
            API.updateProviderClientInputs(parseInt(id ?? ''), {ui_inputs: clientInputs?.length === 0 ? null : clientInputs}).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.build_inputs.success_message', {provider}))).catch((e) => {
                ErrorHandler(e, `put${APILink.UPDATE_PROVIDER_CLIENT_INPUTS.replace('$provider', id?.toString() ?? '')}`, toast, handleError)
                setInProgress(false)
            });
        } else {
            API.updateProviderCredentialsInputs(parseInt(id ?? ''), {
                inputs: credentialsInputs?.length === 0 ? null : credentialsInputs,
                inputs_for_currencies: credentialsCurrencyInputs?.length === 0 ? null : credentialsCurrencyInputs
            }).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.build_inputs.success_message', {provider}))).catch((e) => {
                ErrorHandler(e, `put${APILink.UPDATE_PROVIDER_CREDENTIALS_INPUTS.replace('$provider', id?.toString() ?? '')}`, toast, handleError)
                setInProgress(false)
            });
        }
    }
    const getSingleProvider = useCallback(() => {
        API.getProvider(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const providerResponse = response.data.data as Provider
            setProvider(providerResponse.name);
            setCredentialsInputs(providerResponse.inputs)
            setCredentialsCurrencyInputs(providerResponse.inputs_for_currencies);
            setClientInputs(providerResponse.ui_inputs);
            setFormIsReady(true);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_PROVIDER.replace('$provider', id?.toString() ?? '')}`, toast, handleError));
    }, [setFormIsReady, toast, handleError, id]);

    const detectInput = useCallback((type: BuilderInputType) => {
        switch (type) {
            case "client":
                return {
                    value: clientInputs,
                    set: setClientInputs
                };
            case "credentials":
                return {
                    value: credentialsInputs,
                    set: setCredentialsInputs
                }
            default:
                return {
                    value: credentialsCurrencyInputs,
                    set: setCredentialsCurrencyInputs
                }
        }
    }, [clientInputs, credentialsInputs, credentialsCurrencyInputs]);

    const onInputTypeChanged = useCallback((s: InputType, input: InputForm, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (!inputs.value)
            return;

        const copy = [...inputs.value];
        for (let i = 0; i < copy.length; i++) {
            // @ts-expect-error can be undefined
            if (copy[i] && copy[i].key === input.key) {
                // @ts-expect-error can be undefined
                copy[i] = {...copy[i], type: s}
            }
        }
        inputs.set(copy);
    }, [detectInput]);

    const onInputKeyChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined, index: number, field: string, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (e?.currentTarget && inputs.value) {
            const copy = [...inputs.value];
            if (copy[index]) {
                // @ts-expect-error can be undefined
                copy[index] = {...copy[index], [field as keyof typeof copy]: e.currentTarget.value};
            }
            inputs.set(copy);
        }
    }, [detectInput]);

    const onDependsChanged = useCallback((s: string, input: InputForm, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (!inputs.value)
            return;
        const copy = [...inputs.value];
        for (let i = 0; i < copy.length; i++) {
            // @ts-expect-error can be undefined
            if (copy[i] && copy[i].key === input.key) {
                // @ts-expect-error can be undefined
                copy[i] = {...copy[i], depends: s}
            }
        }
        inputs.set(copy);
    }, [detectInput]);

    const addField = useCallback((type: BuilderInputType) => {
        const inputs = detectInput(type);
        const field = {
            key: "",
            label: "",
            type: InputType.input
        };
        const fields = inputs.value ? [...inputs.value, field] : [field]
        inputs.set(fields);
    }, [detectInput]);

    const onRemoveField = useCallback((index: number, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (!inputs.value)
            return;
        const copy = [...inputs.value];
        if (copy[index])
            copy.splice(index, 1);

        inputs.set(copy ? copy : []);
    }, [detectInput]);

    const onOptionChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined, index: number, optionID: number, optionKey: string, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (e?.currentTarget && inputs.value) {
            const copy = [...inputs.value];
            if (copy[index]) {
                console.log(copy[index]);
                // @ts-expect-error can be undefined
                if (copy[index].options) {
                    // @ts-expect-error can be undefined
                    const newOptions = copy[index].options;
                    if (newOptions){
                        const opt = newOptions[optionID];
                        if (opt){
                            newOptions[optionID] = {
                                ...opt,
                                [optionKey as keyof typeof opt]: e.currentTarget.value
                            };
                        }
                    }
                    // @ts-expect-error can be undefined
                    copy[index] = {...copy[index], options: newOptions};
                }
            }
            inputs.set(copy);
        }
    }, [detectInput]);

    const addOption = useCallback((index: number, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (inputs.value) {
            const fields = [...inputs.value];
            if (fields[index]) {
                const field = {
                    id: "",
                    name: ""
                };
                // @ts-expect-error can be undefined
                if (!fields[index].options) {
                    // @ts-expect-error can be undefined
                    fields[index].options = [];
                }
                // @ts-expect-error can be undefined
                fields[index].options.push(field);
                inputs.set(fields);
            }
        }
    }, [detectInput]);

    const removeOption = useCallback((index: number, optionID: number, type: BuilderInputType) => {
        const inputs = detectInput(type);
        if (inputs.value) {
            const fields = [...inputs.value];
            if (fields[index]) {
                // @ts-expect-error can be undefined
                fields[index].options.splice(optionID, 1)
                inputs.set(fields);
            }
        }
    }, [detectInput]);

    const dependsLabels = useCallback((index: number, key: string, type: BuilderInputType) => {
        const inputs = detectInput(type);
        return [{code: '', name: '-------'}].concat(variables.map((item) => {
            return {code: item, name: item}
        }).concat((inputs.value ?? []).slice(0, index).map((item) => {
            return {code: item.key, name: item.key}
        }))).filter(i => i.code !== key);
    }, [detectInput]);

    useEffect(() => {
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                setType(window.location.pathname.includes('credentials') ? "credentials" : "client");
                if (id) {
                    getSingleProvider();
                } else {
                    toast?.show('Wrong provider');
                    return;
                }
            }).catch();
        }
    }, [id, user]);

    const credentialsFormUI = useMemo(() => {
        if (type !== "credentials" || !formIsReady)
            return <></>;

        return <div className="max-w-md">
            <div className="grid grid-cols-2">
                <h3 className="text-lg font-bold">{t('admin.build_inputs.currency_inputs')}</h3>
                <div className="text-right">
                    <Button size="sm" className="bg-black text-white"
                            onClick={() => addField(BuilderInputType.currency)}>{t('admin.build_inputs.add_field')}</Button>
                </div>
            </div>
            <h4 className="mb-16 text-sm text-gray-400">{t('admin.build_inputs.repeatable_inputs')}</h4>
            <InputBuilder validated={validated} inputs={credentialsCurrencyInputs} type={BuilderInputType.currency}
                          dependsLabels={dependsLabels}
                          onInputTypeChanged={onInputTypeChanged}
                          onInputKeyChanged={onInputKeyChanged}
                          onDependsChanged={onDependsChanged}
                          onOptionChanged={onOptionChanged}
                          addOption={addOption}
                          removeOption={removeOption}
                          removeField={onRemoveField}
            />

            <div className="grid grid-cols-2 mt-16 mb-16">
                <h3 className="text-lg font-bold">{t('admin.build_inputs.single_inputs')}</h3>
                <div className="text-right">
                    <Button size="sm" className="bg-black text-white"
                            onClick={() => addField(BuilderInputType.credentials)}>{t('admin.build_inputs.add_field')}</Button>
                </div>
            </div>
            <InputBuilder validated={validated} inputs={credentialsInputs} type={BuilderInputType.credentials}
                          dependsLabels={dependsLabels}
                          onInputTypeChanged={onInputTypeChanged}
                          onInputKeyChanged={onInputKeyChanged}
                          onDependsChanged={onDependsChanged}
                          onOptionChanged={onOptionChanged}
                          addOption={addOption}
                          removeOption={removeOption}
                          removeField={onRemoveField}
            />
        </div>
    }, [type, formIsReady, credentialsCurrencyInputs, validated, dependsLabels, onInputTypeChanged, onInputKeyChanged, onDependsChanged, onOptionChanged, removeOption, addOption, addField, onRemoveField]);

    const clientFormUI = useMemo(() => {
        if (type !== "client" || !formIsReady)
            return <></>;

        return <div className="max-w-md">
            <div className="grid grid-cols-2 mb-16">
                <h3 className="text-lg font-bold">{t('admin.build_inputs.inputs')}</h3>
                <div className="text-right">
                    <Button size="sm" className="bg-black text-white"
                            onClick={() => addField(BuilderInputType.client)}>{t('admin.build_inputs.add_field')}</Button>
                </div>
            </div>

            <InputBuilder validated={validated} inputs={clientInputs} type={BuilderInputType.client}
                          dependsLabels={dependsLabels}
                          onInputTypeChanged={onInputTypeChanged}
                          onInputKeyChanged={onInputKeyChanged}
                          onDependsChanged={onDependsChanged}
                          onOptionChanged={onOptionChanged}
                          addOption={addOption}
                          removeOption={removeOption}
                          removeField={onRemoveField}
            />
        </div>
    }, [type, formIsReady, clientInputs, dependsLabels, validated, addField, onInputTypeChanged, onInputKeyChanged, onDependsChanged, onOptionChanged, addOption, removeOption, onRemoveField]);


    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <div className="grid grid-cols-1 mt-4 items-center">
                    <div>
                        <h2 className="font-bold text-2xl lg:text-4xl">{t('admin.build_inputs.title', {type})}</h2>
                    </div>
                </div>
                <Card className="mt-12 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {credentialsFormUI}
                        {clientFormUI}
                        <div className="mt-12">
                            <Button onClick={() => submit()} className="text-white bg-teal-500"
                                    disabled={isInProgress}>{t('admin.build_inputs.update')}</Button>
                        </div>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(InputBuilderForm);
